import { useEffect, useMemo, useRef } from 'react';
import { mileToKm } from 'shared/modules/loadboard/LoadboardUtils';
import { createSearchCriteria } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { PostingDTO } from '../../data/dto/PostingDTO';
import { PostingLoadDTO } from '../../data/dto/PostingLoadDTO';
import { useSearchPostingsList } from '../../data/LoadboardPostingAPI';
import { trackLoadboardEvent } from '../LoadboardAnalytics';

export const NEARBY_SHIPPER_LOADS_RADIUS_MILES = 50;
export const NEARBY_SHIPPER_LOADS_DESKTOP_LIMIT = 5;
export const NEARBY_SHIPPER_LOADS_MOBILE_LIMIT = 3;

export function createNearbyShipperLoadsCriteria(load: PostingLoadDTO) {
  const radius = mileToKm(NEARBY_SHIPPER_LOADS_RADIUS_MILES);

  return createSearchCriteria({
    shipper: load.shipper
      ? {
          include: [
            { guid: load.shipper.guid, display_name: load.shipper.name },
          ],
          exclude: [],
        }
      : undefined,
    pickup_venues: [load.pickup.venue],
    delivery_venues: [load.delivery.venue],
    pickup_radius: radius,
    delivery_radius: radius,
  });
}

/**
 * The searched load itself is always part of the results, so we request one
 * extra posting and drop it before applying the preview limit.
 */
export function useNearbyShipperLoads(load: PostingLoadDTO, limit: number) {
  const criteria = useMemo(
    () => createNearbyShipperLoadsCriteria(load),
    [load],
  );

  const { data, isLoading } = useSearchPostingsList(
    {
      page_sort: ['pickup_distance,asc', 'posted_to_loadboard_at,desc'],
      current_page_size: limit + 1,
      search_criteria: criteria,
    },
    // This search is derived from the viewed load, not from the carrier's own
    // search, so its distances must not leak into any load details.
    { shouldCacheSearchResults: false },
  );

  const nearbyLoads = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.data)
        .filter((posting) => posting.load.guid !== load.guid)
        .slice(0, limit),
    [data, load.guid, limit],
  );

  return { criteria, nearbyLoads, isLoading };
}

export function useTrackNearbyShipperLoadsViewed(
  posting: PostingDTO,
  resultsCount: number,
) {
  const reportedLoadGuidRef = useRef<string>();

  useEffect(() => {
    if (
      resultsCount === 0 ||
      reportedLoadGuidRef.current === posting.load.guid
    ) {
      return;
    }

    reportedLoadGuidRef.current = posting.load.guid;
    trackLoadboardEvent({
      name: 'SLB: Viewed Nearby Shipper Loads',
      posting,
      resultsCount,
    });
  }, [posting, resultsCount]);
}
